import React, {useEffect, useRef, useState} from 'react';
import {useQuery} from "@tanstack/react-query";
import storageStatusService from "../../../../services/web/storage-status-service.js";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import {getMonths} from "../utils/get-months.js";
import {AgCharts} from "ag-charts-react";
import {
    CategoryAxisModule,
    LegendModule,
    LineSeriesModule,
    ModuleRegistry,
    NumberAxisModule,
    TimeAxisModule
} from "ag-charts-community";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import HeaderTemplateButton from "../../../../components/button/header-template-button.jsx";
import {getAssetPath} from "../../../../utils/get-asset-path.js";
import {useReactToPrint} from "react-to-print";

ModuleRegistry.registerModules([
    CategoryAxisModule,
    LegendModule,
    LineSeriesModule,
    NumberAxisModule,
    TimeAxisModule
]);

const StorageStatusDetails = ({show, setShow, archiverSelected, archivers}) => {
    const archiver = archivers.find(a => a.archiverId === archiverSelected);

    const [dates, setDates] = useState({});
    const [events, setEvents] = useState([]);
    const [headerSelected, setHeaderSelected] = useState(undefined);

    const contentRef = useRef(undefined);

    const {data} = useQuery({
        queryFn: () => storageStatusService.archiver(archiverSelected).then(({data}) => data.calendar.map(c => ({
            ...c,
            day: new Date(c.day)
        }))),
        queryKey: ['time-line', [archiverSelected]],
        enabled: show && !!archiverSelected
    });

    const [options, setOptions] = useState({
        title: {
            text: "",
        },
        data: [],
        series: [
            {
                type: "line",
                xKey: "day",
                yKey: "totalPassages",
                yName: "Total de passagens",
                marker: {enabled: false},
                tooltip: {
                    renderer: ({datum: {day, totalPassages}, yKey, yName, xKey}) => {
                        return {
                            data: [
                                {
                                    label: "Passagens " + new Date(day).toLocaleString().replace(",", ""),
                                    value: totalPassages.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')
                                },
                            ],
                        };
                    }
                }
            },
            {
                type: "line",
                xKey: "day",
                yKey: "totalBytes",
                yName: "Total de bytes",
                marker: {enabled: false},
                tooltip: {
                    renderer: ({datum: {day, totalBytes}, yKey, yName, xKey}) => {
                        return {
                            data: [
                                {
                                    label: "Bytes " + new Date(day).toLocaleString().replace(",", ""),
                                    value: totalBytes.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')
                                },
                            ],
                        };
                    }
                },
                visible: false
            },
        ],
        axes: {
            x: {
                title: {
                    text: "Mês",
                },
                label: {
                    formatter: (params) => {
                        const options = {month: "short"};
                        return new Intl.DateTimeFormat("pt-BR", options).format(new Date(params.value));
                    },
                },
            },
            y: {
                title: {
                    text: "Total",
                },
                label: {
                    formatter: ({value}) => value.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.'),
                },
            },
        }
    });

    useEffect(() => {
        if (data) setOptions(prev => ({...prev, data}));
    }, [data]);

    useEffect(() => {
        if (!archiver) return;

        const {minDtWithImage, maxDtWithImage, minDtWithoutImage, maxDtWithoutImage} = archiver;

        const dtWithImage = getEvents(minDtWithImage, maxDtWithImage, "#deffdd");
        const dtWithoutImage = getEvents(minDtWithoutImage, maxDtWithoutImage, "#fcdede");
        const mergedDates = mergeEvents(dtWithImage, dtWithoutImage);

        const validateDate = (dt1, dt2, target) => {
            if (dt1 && dt2) {
                const date = target === "min" ?
                    Math.min(new Date(dt1), new Date(dt2)) :
                    Math.max(new Date(dt1), new Date(dt2));

                return new Date(date).toISOString().split("T")[0];
            }

            if (!dt1 && dt2) return new Date(dt2).toISOString().split("T")[0];
            if (dt1 && !dt2) return new Date(dt1).toISOString().split("T")[0];

            if (!dt1 && !dt2) return new Date().toISOString().split("T")[0];
        }

        const minDateStr = validateDate(archiver.minDtWithImage, archiver.minDtWithoutImage, "min");
        const maxDateStr = validateDate(archiver.maxDtWithImage, archiver.maxDtWithoutImage, "max");

        setDates(getMonths(new Date(minDateStr), new Date(maxDateStr)));
        setEvents(mergedDates);
    }, [archiverSelected]);

    const formatDate = (date) => date.toISOString().split("T")[0];

    const getEvents = (startDateStr, endDateStr, color) => {
        if (!startDateStr || !endDateStr) return [];

        const startDate = new Date(startDateStr);
        const endDate = new Date(endDateStr);
        const result = [];

        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            result.push({
                start: formatDate(d),
                end: formatDate(d),
                display: "background",
                color,
            });
        }
        return result;
    }

    const mergeEvents = (dtWithImage, dtWithoutImage) => {
        const mergedMap = new Map();

        dtWithImage.forEach((item) => mergedMap.set(item.start, item));

        dtWithoutImage.forEach((item) => {
            if (mergedMap.has(item.start)) {
                const existing = mergedMap.get(item.start);
                mergedMap.set(item.start, {...existing, color: "#deffdd"});
            } else {
                mergedMap.set(item.start, item);
            }
        });

        return Array.from(mergedMap.values());
    };

    const print = useReactToPrint({
        contentRef,
        documentTitle: "Status de armazenamento",
    });

    const modalTitle = (
        <div className={"flex justify-between w-full mr-4"}>
            <span>Detalhes - Archiver {archiverSelected}</span>

            <div className={"flex gap-2"}>
                <Button onClick={print}>
                    Imprimir
                </Button>
                <HeaderTemplateButton
                    selected={headerSelected}
                    setSelected={setHeaderSelected}
                />
            </div>
        </div>
    );

    return (
        <Modal className={"w-11/12"} open={show} setOpen={setShow} isCloseClick={false} title={modalTitle}>
            <Modal.Body className="max-h-[calc(100vh-220px)] overflow-auto">
                <div className={"storage-status px-5 print:p-5"} ref={contentRef}>
                    <table>
                        <thead>
                        <tr>
                            <td>
                                <div className="flex wrap w-full text-gray-600 relative mt-2">
                                    <div className={"absolute w-[100px] h-[89px] justify-items-center"}>
                                        <img
                                            className={"w-auto max-h-[85px]"}
                                            src={headerSelected?.imagePath}
                                            alt=""
                                        />
                                    </div>

                                    <div className="flex flex-col justify-center items-center w-full h-[90px]">
                                        <p className="text-xl font-bold text-center text-black w-full">{headerSelected?.name ?? ""}</p>
                                        <p className="text-[12px] font-light text-black text-center w-full">{headerSelected?.addressFormated}</p>
                                        <p className="text-sm font-light text-center text-black w-full uppercase mt-1">Status de armazenamento</p>
                                    </div>
                                </div>
                                <hr className={"my-2 text-[#e5e7eb]"}/>
                            </td>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td className={"space-y-3 px-5"}>
                                {
                                    data &&
                                    <>
                                        <h4>Passagens de veículos</h4>
                                        <div
                                            className="relative flex flex-col h-full text-gray-700 bg-white rounded-lg bg-clip-border w-full">
                                            <table className="text-left table-auto text-slate-800">
                                                <thead>
                                                <tr className="border-b border-[var(--default-border)]">
                                                    <th className="px-4 py-2" style={{width: "10%"}}>
                                                        <p className="text-sm leading-none font-semibold">
                                                            Servidor
                                                        </p>
                                                    </th>
                                                    <th className="px-4 py-2" style={{width: "10%"}}>
                                                        <p className="text-sm leading-none font-semibold">
                                                            Espaço total
                                                        </p>
                                                    </th>
                                                    <th className="px-4 py-2" style={{width: "10%"}}>
                                                        <p className="text-sm leading-none font-semibold">
                                                            Utilizado
                                                        </p>
                                                    </th>
                                                    <th className="px-4 py-2" style={{width: "20%"}}>
                                                        <p className="text-sm leading-none font-semibold">
                                                            Com imagens (Rotativo)
                                                        </p>
                                                    </th>
                                                    <th className="px-4 py-2" style={{width: "20%"}}>
                                                        <p className="text-sm leading-none font-semibold">
                                                            Sem imagens (Expurgadas)
                                                        </p>
                                                    </th>
                                                    <th className="px-4 py-2" style={{width: "30%"}}>
                                                        <p className="text-sm leading-none font-semibold">
                                                            Com imagens (Protegidas)
                                                        </p>
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                <tr className="bg-[#f3f3f4]">
                                                    <td className="px-4 py-2">
                                                        <p className="text-sm">
                                                            {archiver?.name}
                                                        </p>
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <p className="text-sm">
                                                            {archiver?.totalDiskFormated}
                                                        </p>
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <p className="text-sm">
                                                            {archiver?.totalDiskUsed}
                                                        </p>
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <p className="text-sm">
                                                            {archiver?.withImage}
                                                        </p>
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <p className="text-sm">
                                                            {archiver?.withoutImage}
                                                        </p>
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <p className="text-sm">
                                                            {archiver?.protectImage}
                                                        </p>
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        <hr className={"opacity-10"}/>
                                        <h4>Linha do tempo</h4>
                                        <div className={"w-[60rem] justify-self-center"}>
                                            <AgCharts options={options}/>
                                        </div>

                                        <hr className={"opacity-10"}/>
                                        <h4>Calendário</h4>
                                        <div className="calendars">
                                            {
                                                Object.keys(dates).map((year) =>
                                                    dates[year].map((month) =>
                                                        <div className="calendar-div"
                                                             key={year + '-' + month}>
                                                            <FullCalendar
                                                                headerToolbar={{
                                                                    left: '',
                                                                    center: 'title',
                                                                    right: ''
                                                                }}
                                                                initialDate={`${year}-${month}-01`}
                                                                height={248}
                                                                contentHeight={185}
                                                                plugins={[dayGridPlugin]}
                                                                initialView={'dayGridMonth'}
                                                                events={events}
                                                                eventDisplay={"none"}
                                                                displayEventTime={false}
                                                                navLinks={false}
                                                                locale={ptBrLocale}
                                                            />
                                                        </div>
                                                    )
                                                )
                                            }
                                        </div>
                                        <hr className={"opacity-10"}/>
                                        <div className="flex justify-center gap-2 mb-2">
                                            <p>
                                                <i className="bg-[#deffdd] p-1 text-xs rounded-sm me-2">{events.filter(e => e.color === '#deffdd').length}</i>
                                                Com imagens
                                            </p>
                                            <p>
                                                <i className="bg-[#fcdede] p-1 text-xs rounded-sm me-2">{events.filter(e => e.color === '#fcdede').length}</i>
                                                Imagens expurgadas
                                            </p>
                                        </div>
                                    </>
                                }
                            </td>
                        </tr>
                        </tbody>
                        <tfoot className="report-footer">
                        <tr>
                            <td id="spacer-footer"></td>
                        </tr>
                        </tfoot>
                    </table>
                    <div className="print:fixed print:w-full print:z-1 print:!bottom-0 print:!left-0 print:!ml-0 print:!px-6 print:!pb-4 space-y-2">
                        <div className={"border-t border-t-[#e7e7e7] py-1"}>
                            <div className="flex justify-between mt-3">
                                <img className="h-[8.5mm]" src={getAssetPath("img/logo-sentry/sw.logo.footer.svg")} alt=""/>
                                <span className="text-[13px] content-end text-black">
                                   Impresso em {new Date().toLocaleDateString("pt-BR")} {new Date().toLocaleTimeString("pt-BR", {hour: "2-digit", minute: "2-digit"})}
                                </span>
                            </div>
                            <small className="hidden print:block italic mt-2">
                                Dados estimados, contém variações.
                            </small>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <small className="italic">
                    Dados estimados, contém variações.
                </small>
            </Modal.Footer>
        </Modal>
    );
};

export default StorageStatusDetails;